import React, { useEffect, useMemo, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import ReportRowActionMenu from './ReportRowActionMenu';

const formatHeader = (value) =>
  String(value || '')
    .replace(/[_.]/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const getFieldValue = (row, field) => {
  if (!field) return undefined;
  const key = String(field);
  if (row && Object.prototype.hasOwnProperty.call(row, key)) return row[key];
  if (!key.includes('.')) return row?.[key];
  const segments = String(field)
    .split('.')
    .map((segment) => segment.trim())
    .filter(Boolean);
  return segments.reduce((acc, segment) => (acc == null ? acc : acc[segment]), row);
};

const resolveValue = (value, row, field) => {
  if (row) {
    const nested = getFieldValue(row, field);
    return nested === undefined ? value : nested;
  }
  if (value && typeof value === 'object' && value.row) {
    const nested = getFieldValue(value.row, field);
    if (nested !== undefined) return nested;
    if ('value' in value) return value.value;
  }
  return getFieldValue(value, field);
};

const buildColumns = (fields, rowActions, onRowAction) => {
  const dataColumns = fields.map((field) => ({
    field,
    headerName: formatHeader(field),
    minWidth: 140,
    flex: 1,
    valueGetter: (value, row) => resolveValue(value, row, field),
  }));

  if (!rowActions.length) return dataColumns;

  return [
    {
      field: '__actions',
      headerName: '',
      sortable: false,
      filterable: false,
      width: 60,
      renderCell: (params) => (
        <ReportRowActionMenu
          row={params.row}
          actions={rowActions}
          onAction={onRowAction}
        />
      ),
    },
    ...dataColumns,
  ];
};

const ReportDataGrid = ({
  rows,
  fields,
  hiddenFields,
  rowActions,
  onRowAction,
  loading,
  total,
  paginationModel,
  onPaginationModelChange,
  sortModel,
  onSortModelChange,
  selectionModel,
  onSelectionModelChange,
  enableRowSelection = false,
}) => {
  const shellRef = useRef(null);
  const resolvedFields = fields.length ? fields : Object.keys(rows[0] || {});
  const hiddenSet = useMemo(
    () => new Set(Array.isArray(hiddenFields) ? hiddenFields : []),
    [hiddenFields]
  );
  const columns = useMemo(
    () => buildColumns(resolvedFields, rowActions, onRowAction),
    [resolvedFields, rowActions, onRowAction]
  );
  const columnVisibilityModel = useMemo(() => {
    if (!hiddenSet.size) return undefined;
    return resolvedFields.reduce((acc, field) => {
      if (hiddenSet.has(field)) acc[field] = false;
      return acc;
    }, {});
  }, [hiddenSet, resolvedFields]);

  useEffect(() => {
    // MUI X 7 does not consistently forward test attributes to the virtual
    // scroller slot, so the component owns the one internal lookup the tests need.
    shellRef.current
      ?.querySelector('.MuiDataGrid-virtualScroller')
      ?.setAttribute('data-testid', 'report-data-grid-scroller');
  }, [columns, rows]);

  return (
    <Box ref={shellRef} sx={{ width: '100%' }} data-testid="report-data-grid-shell">
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        Data preview
      </Typography>
      <DataGrid
        data-testid="report-data-grid"
        autoHeight
        rows={rows}
        columns={columns}
        getRowId={(row) => row.__rowId || row.id || row.rowId || row.bac || row.__fallbackId || JSON.stringify(row)}
        loading={loading}
        rowCount={Number.isFinite(total) ? total : rows.length}
        paginationMode="server"
        sortingMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        pageSizeOptions={[10, 25, 50]}
        disableRowSelectionOnClick
        checkboxSelection={enableRowSelection}
        rowSelectionModel={selectionModel}
        onRowSelectionModelChange={onSelectionModelChange}
        columnVisibilityModel={columnVisibilityModel}
      />
    </Box>
  );
};

export default ReportDataGrid;
